import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Text,
  Card,
  Group,
  Loader,
  Stack,
  Button,
  ScrollArea,
  Paper,
  ThemeIcon,
  Alert,
  Center,
  Notification,
} from "@mantine/core";
import { IconWifi, IconFileText, IconCheck, IconX } from "@tabler/icons-react";
import FHIR from "fhirclient";

// Define interfaces for FHIR resources
interface FhirPatient {
  id: string;
  name?: Array<{
    family?: string;
    given?: string[];
    text?: string;
  }>;
  identifier?: Array<{
    system?: string;
    value?: string;
    type?: {
      coding?: Array<{
        system?: string;
        code?: string;
        display?: string;
      }>;
      text?: string;
    };
  }>;
}

interface FhirPractitioner {
  id: string;
  name?: Array<{
    family?: string;
    given?: string[];
    text?: string;
  }>;
  identifier?: Array<{
    system?: string;
    value?: string;
    type?: {
      coding?: Array<{
        system?: string;
        code?: string;
        display?: string;
      }>;
      text?: string;
    };
  }>;
}

interface FhirEncounter {
  id: string;
  status?: string;
  type?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  subject?: {
    reference?: string;
    display?: string;
  };
  participant?: Array<{
    type?: Array<{
      coding?: Array<{
        system?: string;
        code?: string;
        display?: string;
      }>;
      text?: string;
    }>;
    individual?: {
      reference?: string;
      display?: string;
    };
  }>;
  period?: {
    start?: string;
    end?: string;
  };
}

interface FhirDocumentReference {
  resourceType: string;
  id?: string;
  status: string;
  type: {
    coding: Array<{
      system: string;
      code: string;
      display: string;
    }>;
  };
  subject: {
    reference: string;
  };
  content: Array<{
    attachment: {
      contentType: string;
      data?: string;
      title?: string;
    };
  }>;
  author?: Array<{
    reference: string;
  }>;
  context?: {
    encounter?: Array<{
      reference: string;
    }>;
  };
}

export function FhirClientPage() {
  // State declarations
  const [fhirClient, setFhirClient] = useState<any>(null);
  const [patientData, setPatientData] = useState<FhirPatient | null>(null);
  const [practitionerData, setPractitionerData] = useState<FhirPractitioner | null>(null);
  const [encounterData, setEncounterData] = useState<FhirEncounter[]>([]);
  const [currentEncounter, setCurrentEncounter] = useState<FhirEncounter | null>(null);
  const [createdDocumentReference, setCreatedDocumentReference] = useState<FhirDocumentReference | null>(null);
  const [loading, setLoading] = useState({
    smartFhir: true,
    practitioner: false,
    encounter: false,
    documentReference: false,
  });
  const [errors, setErrors] = useState({
    smartFhir: null as string | null,
    patient: null as string | null,
    practitioner: null as string | null,
    encounter: null as string | null,
    documentReference: null as string | null,
  });
  const [expandedJson, setExpandedJson] = useState({
    patient: false,
    practitioner: false,
    encounter: false,
    allEncounters: false,
    documentReference: false,
  });
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'success' | 'error';
    message: string;
  }>>([]);

  // Utility function to update specific error
  const updateError = useCallback((key: keyof typeof errors, value: string | null) => {
    setErrors(prev => ({ ...prev, [key]: value }));
  }, []);

  // Add notification
  const addNotification = (type: 'success' | 'error', message: string) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  // Helper function to extract patient ID
  const getPatientId = useCallback((patientData: any): string | null => {
    try {
      if (patientData?.id) return patientData.id;
      if (patientData?.data?.id) return patientData.data.id;
      if (patientData?.entry?.[0]?.resource?.id) return patientData.entry[0].resource.id;
      if (patientData?.resource?.id) return patientData.resource.id;
      console.warn('Could not extract patient ID from:', patientData);
      return null;
    } catch (error) {
      console.error('Error extracting patient ID:', error);
      return null;
    }
  }, []);

  // Helper function to extract patient resource
  const getPatientResource = useCallback((patientData: any): any => {
    try {
      if (patientData?.resourceType === 'Patient') return patientData;
      if (patientData?.data?.resourceType === 'Patient') return patientData.data;
      if (patientData?.entry?.[0]?.resource?.resourceType === 'Patient') 
        return patientData.entry[0].resource;
      if (patientData?.resource?.resourceType === 'Patient') return patientData.resource;
      if (patientData?.id && (patientData?.name || patientData?.identifier)) 
        return patientData;
      console.warn('Could not extract patient resource from:', patientData);
      return patientData;
    } catch (error) {
      console.error('Error extracting patient resource:', error);
      return patientData;
    }
  }, []);

  // Create DocumentReference
  const createDocumentReference = useCallback(async () => {
    if (!fhirClient || !patientData?.id) {
      updateError('documentReference', 'FHIR client or patient data not available');
      return;
    }

    try {
      setLoading(prev => ({ ...prev, documentReference: true }));
      updateError('documentReference', null);

      // Sample document content (base64 encoded)
      const documentContent = "This is a sample clinical document created for testing purposes. Patient visit summary and treatment notes.";
      const base64Content = btoa(documentContent); // Convert to base64

      // Create DocumentReference resource
      const documentReference: FhirDocumentReference = {
        resourceType: "DocumentReference",
        status: "current",
        type: {
          coding: [{
            system: "http://loinc.org",
            code: "34133-9",
            display: "Summary of episode note"
          }]
        },
        subject: {
          reference: `Patient/${patientData.id}`
        },
        content: [{
          attachment: {
            contentType: "text/plain",
            data: base64Content,
            title: "Sample Clinical Document"
          }
        }]
      };

      // Add author if practitioner data is available
      if (practitionerData?.id) {
        documentReference.author = [{
          reference: `Practitioner/${practitionerData.id}`
        }];
      }

      // Add encounter context if available
      if (currentEncounter?.id) {
        documentReference.context = {
          encounter: [{
            reference: `Encounter/${currentEncounter.id}`
          }]
        };
      }

      console.log('Creating DocumentReference:', documentReference);

      // Create the DocumentReference using FHIR client
      const result = await fhirClient.create(documentReference);
      
      console.log('DocumentReference created successfully:', result);
      setCreatedDocumentReference(result);
      addNotification('success', `DocumentReference created successfully with ID: ${result.id}`);

    } catch (error: any) {
      console.error('Error creating DocumentReference:', error);
      
      let errorMessage = 'Failed to create DocumentReference';
      if (error.response) {
        errorMessage += ` (${error.response.status}: ${error.response.statusText})`;
        if (error.response.data) {
          console.error('Error response data:', error.response.data);
          if (error.response.data.issue) {
            const issues = error.response.data.issue.map((issue: any) => issue.diagnostics || issue.details?.text).filter(Boolean);
            if (issues.length > 0) {
              errorMessage += ` - ${issues.join(', ')}`;
            }
          }
        }
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      updateError('documentReference', errorMessage);
      addNotification('error', errorMessage);
    } finally {
      setLoading(prev => ({ ...prev, documentReference: false }));
    }
  }, [fhirClient, patientData, practitionerData, currentEncounter, updateError]);

  // Fetch practitioner data
  const fetchPractitionerData = useCallback(async (client: any) => {
    try {
      setLoading(prev => ({ ...prev, practitioner: true }));
      updateError('practitioner', null);

      if (!client.user) {
        updateError('practitioner', 'No user information available in FHIR client');
        return null;
      }

      let practitioner: FhirPractitioner | null = null;

      if (client.user.fhirUser) {
        try {
          practitioner = await client.request(client.user.fhirUser);
        } catch (error) {
          console.error('Error fetching practitioner from fhirUser:', error);
        }
      }

      if (!practitioner && client.user.id) {
        try {
          const searchResult = await client.request(`Practitioner?identifier=${client.user.id}`);
          if (searchResult?.entry && searchResult.entry.length > 0) {
            practitioner = searchResult.entry[0].resource;
          }
        } catch (error) {
          console.error('Error searching practitioner by user ID:', error);
        }
      }

      if (!practitioner && client.user.profile) {
        try {
          const profile = client.user.profile;
          let searchParams = [];
          if (profile.family_name && profile.given_name) {
            searchParams.push(`family=${encodeURIComponent(profile.family_name)}`);
            searchParams.push(`given=${encodeURIComponent(profile.given_name)}`);
          } else if (profile.name) {
            searchParams.push(`name=${encodeURIComponent(profile.name)}`);
          }

          if (searchParams.length > 0) {
            const searchQuery = `Practitioner?${searchParams.join('&')}`;
            const searchResult = await client.request(searchQuery);
            if (searchResult?.entry && searchResult.entry.length > 0) {
              practitioner = searchResult.entry[0].resource;
            }
          }
        } catch (error) {
          console.error('Error searching practitioner by profile:', error);
        }
      }

      if (practitioner) {
        setPractitionerData(practitioner);
        return practitioner;
      } else {
        updateError('practitioner', 'Could not retrieve practitioner information');
        return null;
      }
    } catch (error) {
      console.error('Error fetching practitioner data:', error);
      updateError('practitioner', 'Failed to fetch practitioner data from FHIR server');
      return null;
    } finally {
      setLoading(prev => ({ ...prev, practitioner: false }));
    }
  }, [updateError]);

  // Fetch encounter data
  const fetchEncounterData = useCallback(async (client: any, patientId: string, practitionerId?: string) => {
    try {
      setLoading(prev => ({ ...prev, encounter: true }));
      updateError('encounter', null);

      let searchParams = [`patient=${patientId}`];
 

      const searchQuery = `Encounter?${searchParams}`;
      const encounterBundle = await client.request(searchQuery);

      if (encounterBundle?.entry && encounterBundle.entry.length > 0) {
        const encounters: FhirEncounter[] = encounterBundle.entry.map((entry: any) => entry.resource);
        setEncounterData(encounters);
        const activeEncounter = encounters.find(enc => enc.status === 'in-progress') || encounters[0];
        if (activeEncounter) {
          setCurrentEncounter(activeEncounter);
        }
        return encounters;
      } else {
        setEncounterData([]);
        setCurrentEncounter(null);
        updateError('encounter', 'No encounters found for patient');
        return [];
      }
    } catch (error) {
      console.error('Error fetching encounter data:', error);
      updateError('encounter', 'Failed to fetch encounter data from FHIR server');
      setEncounterData([]);
      setCurrentEncounter(null);
      return [];
    } finally {
      setLoading(prev => ({ ...prev, encounter: false }));
    }
  }, [updateError]);

  // Get practitioner display name
  const getPractitionerName = useCallback((practitioner: FhirPractitioner | null): string => {
    if (!practitioner?.name || practitioner.name.length === 0) {
      return 'Unknown Practitioner';
    }
    const name = practitioner.name[0];
    if (name.text) return name.text;
    let displayName = '';
    if (name.given && name.given.length > 0) {
      displayName = name.given.join(' ');
    }
    if (name.family) {
      displayName = displayName ? `${displayName} ${name.family}` : name.family;
    }
    return displayName || 'Unknown Practitioner';
  }, []);

  // Get encounter display information
  const getEncounterInfo = useCallback((encounter: FhirEncounter | null): string => {
    if (!encounter) return 'No active encounter';
    let info = `Encounter ${encounter.id}`;
    if (encounter.type && encounter.type.length > 0) {
      const typeDisplay = encounter.type[0].text || 
                         encounter.type[0].coding?.[0]?.display || 
                         'Unknown Type';
      info += ` (${typeDisplay})`;
    }
    if (encounter.period?.start) {
      const startDate = new Date(encounter.period.start);
      info += ` - ${startDate.toLocaleDateString()}`;
    }
    return info;
  }, []);

  // Initialize SMART on FHIR client
  useEffect(() => {
    const initializeFhirClient = async () => {
      try {
        setLoading(prev => ({ ...prev, smartFhir: true }));
        updateError('smartFhir', null);

        const client = await FHIR.oauth2.ready();
        setFhirClient(client);

        // Fetch patient data
        const rawPatientData = await client.patient.read<any>();
        const patientResource = getPatientResource(rawPatientData);
        const patientId = getPatientId(rawPatientData);

        if (!patientId) {
          throw new Error('Could not extract patient ID from FHIR response');
        }

        setPatientData(patientResource);

        // Fetch practitioner data
        const practitioner = await fetchPractitionerData(client);

        // Fetch encounter data
        await fetchEncounterData(client, patientId, practitioner?.id);

      } catch (error) {
        console.error('Error initializing SMART on FHIR:', error);
        updateError('smartFhir', 'Failed to initialize SMART on FHIR client');
      } finally {
        setLoading(prev => ({ ...prev, smartFhir: false }));
      }
    };

    initializeFhirClient();
  }, [fetchEncounterData, fetchPractitionerData, getPatientId, getPatientResource, updateError]);

  // JSON Viewer component
  const JsonViewer = ({
    data,
    title,
    isExpanded,
    onToggle,
    loading = false,
  }: {
    data: any;
    title: string;
    isExpanded: boolean;
    onToggle: () => void;
    loading?: boolean;
  }) => (
    <Card withBorder mb="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <Text fw={500} size="sm">{title}</Text>
          {loading && <Loader size="xs" />}
        </Group>
        <Button
          variant="subtle"
          size="xs"
          onClick={onToggle}
          disabled={loading || !data}
        >
          {isExpanded ? 'Hide JSON' : 'Show JSON'}
        </Button>
      </Group>

      {!isExpanded && data && (
        <Box mb="sm">
          {title === 'Patient Data' && (
            <Text size="sm" c="dimmed">
              {data.name?.[0]?.text ||
                `${data.name?.[0]?.given?.join(' ') || ''} ${data.name?.[0]?.family || ''}`.trim() ||
                'Unknown Patient'}
              {data.identifier?.find((id: any) => id.type?.text === "MRN")?.value &&
                ` (MRN: ${data.identifier.find((id: any) => id.type?.text === "MRN").value})`}
            </Text>
          )}
          {title === 'Practitioner Data' && (
            <Text size="sm" c="dimmed">
              {getPractitionerName(data)}
            </Text>
          )}
          {title === 'Current Encounter' && (
            <Text size="sm" c="dimmed">
              {getEncounterInfo(data)}
            </Text>
          )}
          {title === 'Created DocumentReference' && (
            <Text size="sm" c="dimmed">
              DocumentReference ID: {data.id} - {data.type?.coding?.[0]?.display || 'Unknown Type'}
            </Text>
          )}
        </Box>
      )}

      {isExpanded && data && (
        <ScrollArea h={400}>
          <Paper p="md" bg="gray.0" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {JSON.stringify(data, null, 2)}
            </pre>
          </Paper>
        </ScrollArea>
      )}

      {!loading && !data && (
        <Text size="sm" c="dimmed" fs="italic">
          No {title.toLowerCase()} available
        </Text>
      )}

      {loading && (
        <Center p="md">
          <Stack align="center" gap="xs">
            <Loader size="sm" />
            <Text lineClamp={2} size="xs" c="dimmed">Loading {title.toLowerCase()}...</Text>
          </Stack>
        </Center>
      )}
    </Card>
  );

  // Loading state
  if (loading.smartFhir) {
    return (
      <Center style={{ height: "100vh" }}>
        <Stack align="center">
          <Loader size="md" />
          <Text size="lg" fw={500}>Initializing FHIR Client</Text>
          <Text size="sm" c="dimmed">Establishing secure connection...</Text>
        </Stack>
      </Center>
    );
  }

  // Error state
  if (errors.smartFhir && !patientData) {
    return (
      <Center style={{ height: "100vh" }}>
        <Card withBorder style={{ maxWidth: 600, width: "100%" }}>
          <Group align="flex-start" gap="md">
            <ThemeIcon size="lg" variant="light" color="red">
              <IconWifi size={20} />
            </ThemeIcon>
            <Stack gap="xs" style={{ flex: 1 }}>
              <Text fw={600} size="sm" c="red">FHIR Connection Failed</Text>
              <Text size="sm" c="dimmed">{errors.smartFhir}</Text>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconWifi size={14} />}
                onClick={() => window.location.reload()}
              >
                Retry Connection
              </Button>
            </Stack>
          </Group>
        </Card>
      </Center>
    );
  }

  return (
    <Box p="md" style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Notifications */}
      {notifications.map((notification) => (
        <Notification
          key={notification.id}
          icon={notification.type === 'success' ? <IconCheck size={18} /> : <IconX size={18} />}
          color={notification.type === 'success' ? 'green' : 'red'}
          title={notification.type === 'success' ? 'Success' : 'Error'}
          onClose={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
          style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000, maxWidth: 400 }}
        >
          {notification.message}
        </Notification>
      ))}

      <Text fw={600} size="xl" mb="md">FHIR Client Dashboard</Text>

      {errors.smartFhir && (
        <Alert color="red" mb="md" title="FHIR Warning">
          {errors.smartFhir}
        </Alert>
      )}

      {/* DocumentReference Creation Section */}
      <Card withBorder mb="md" style={{ backgroundColor: '#f8f9fa' }}>
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconFileText size={14} />
              </ThemeIcon>
              <Text fw={500} size="sm">Create DocumentReference</Text>
            </Group>
            <Text size="xs" c="dimmed">
              Create a sample clinical document for the current patient
              {practitionerData && ` authored by ${getPractitionerName(practitionerData)}`}
              {currentEncounter && ` in the context of encounter ${currentEncounter.id}`}
            </Text>
            {errors.documentReference && (
              <Alert color="red"  title="Error">
                {errors.documentReference}
              </Alert>
            )}
          </Stack>
          <Button
            leftSection={<IconFileText size={16} />}
            onClick={createDocumentReference}
            loading={loading.documentReference}
            disabled={!fhirClient || !patientData?.id}
            size="sm"
          >
            Create Document
          </Button>
        </Group>
      </Card>

      <JsonViewer
        data={patientData}
        title="Patient Data"
        isExpanded={expandedJson.patient}
        onToggle={() => setExpandedJson(prev => ({ ...prev, patient: !prev.patient }))}
        loading={loading.smartFhir}
      />

      <JsonViewer
        data={practitionerData}
        title="Practitioner Data"
        isExpanded={expandedJson.practitioner}
        onToggle={() => setExpandedJson(prev => ({ ...prev, practitioner: !prev.practitioner }))}
        loading={loading.practitioner}
      />

      <JsonViewer
        data={currentEncounter}
        title="Current Encounter"
        isExpanded={expandedJson.encounter}
        onToggle={() => setExpandedJson(prev => ({ ...prev, encounter: !prev.encounter }))}
        loading={loading.encounter}
      />

      {createdDocumentReference && (
        <JsonViewer
          data={createdDocumentReference}
          title="Created DocumentReference"
          isExpanded={expandedJson.documentReference}
          onToggle={() => setExpandedJson(prev => ({ ...prev, documentReference: !prev.documentReference }))}
        />
      )}

      {encounterData.length > 1 && (
        <Card withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={500} size="sm">All Encounters ({encounterData.length})</Text>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setExpandedJson(prev => ({ ...prev, allEncounters: !prev.allEncounters }))}
            >
              {expandedJson.allEncounters ? 'Hide All' : 'Show All'}
            </Button>
          </Group>

          {expandedJson.allEncounters ? (
            <ScrollArea h={400}>
              <Paper p="md" bg="gray.0" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                  {JSON.stringify(encounterData, null, 2)}
                </pre>
              </Paper>
            </ScrollArea>
          ) : (
            <Stack gap="xs">
              {encounterData.map((encounter, index) => (
                <Text key={encounter.id} size="sm" c="dimmed">
                  {index + 1}. {getEncounterInfo(encounter)}
                </Text>
              ))}
            </Stack>
          )}
        </Card>
      )}
    </Box>
  );
}